import React, { PropsWithChildren, ReactNode, RefObject, useId, useRef, useState} from 'react';
import clsx from 'clsx';

import { Icon, type IconType } from '@/stories/atoms/Icon/Icon';
import { useClickOutside } from '@/hooks/useClickOutside';

import labelStyles from '@/stories/components/Form/FormItem/label.module.css'
import styles from '@/stories/components/Form/Input/input.module.css'
import dropdown from './dropdown.module.css'
import { FormItem, FormItemProps } from '../FormItem/FormItem';


export type DropdownProps = {
  /** Determine if the Accordion is open by default */
  isOpen?: boolean,
  title?: string | ReactNode,
  label?: string,
  hideLabel?: boolean,
  variant?: 'secondary'
  icon?: IconType,
  className?: string,
  panelClassName?: string
} & PropsWithChildren

export const Dropdown = ({label, hideLabel, title, isOpen= false, variant, icon, className, panelClassName, children, ...props}: DropdownProps)=> {
  const [ open, setOpen ] = useState(isOpen)
  const id = useId()
  const ref =  useRef(null)
  useClickOutside(ref, () => setOpen(false), open)

  return (
    <DropdownWpr id={id} open={open} setOpen={setOpen} className={clsx(className)} label={label} hideLabel={hideLabel} {...props}>
      <DropdownButton
        id={id}
        icon={icon}
        title={title}
        variant={variant}
        open={open}
        setOpen={setOpen}/>
      <DropdownPanel ref={ref} id={`${id}`} open={open} className={panelClassName}>
        {children}
      </DropdownPanel>
    </DropdownWpr>
  )
}

type DropdownButtonProps = {
  open: boolean,
  setOpen: (open: boolean) => void
  id: string
  title: string | ReactNode,
  icon?: IconType,
  variant?: 'secondary'
}

export const DropdownButton = ({open, setOpen, icon, id, title, variant}: DropdownButtonProps) => {
  const toggleMenu = () => {
    setOpen(!open)
  }

  return (
    <div className={clsx(styles.wrapper, dropdown.btnWrapper, icon && 'has-icon')}>
      {icon ? <Icon icon={icon} className={styles.icon}/> : null}
      <button type='button' className={clsx(dropdown.button, styles.input, styles.select, variant && styles[variant])} id={id} onAuxClick={toggleMenu} onClick={toggleMenu} aria-expanded={open}>
        {title}
      </button>
      <Icon icon={'angle-down'} className={styles.carret}/>
    </div>
  )
}

type DropdownPanelProps = {
  open: boolean,
  ref?: RefObject<HTMLDivElement| null>,
  id: string,
  className?: string
} & PropsWithChildren

export const DropdownPanel = ({open, ref, id, children, className, ...props}: DropdownPanelProps) => {
  return (
    <div className={clsx(className, dropdown.panel)} ref={ref} hidden={!open} aria-labelledby={id} {...props}>
      {children}
    </div>
  )
}


type DropdownWprProps = {
  id: string,
  label?: string,
  hideLabel?: boolean,
  open: boolean,
  setOpen: (open: boolean) => void
  className?: string,
} & Omit<FormItemProps, 'ref'> & PropsWithChildren

export const DropdownWpr = ({id, label, hideLabel, open, setOpen, className, children, ...props}: DropdownWprProps) => {
  const toggleMenu = () => {
    setOpen(!open)
  }

  return (
    <FormItem className={className} {...props} id={`wrapper-${id}`}>
      {label ? <span onAuxClick={toggleMenu} onClick={toggleMenu} className={clsx(labelStyles.label, hideLabel && 'sr-only')}>{label}</span> : null}
      {children}
    </FormItem>
  )
}